import React from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Barra di ricerca del Centro Guida con dropdown risultati.
 *
 * Il componente e' controllato: la logica di scoring e' interamente lato
 * backend (foundation.guide.search). Qui gestiamo solo input + rendering.
 *
 * Props:
 *  - value, onChange
 *  - results: [{article_id, article_title, chapter_id, chapter_title, highlights[], topic}]
 *  - loading: bool
 *  - onSelect(chapter_id, article_id)
 *  - placeholder?
 */
export function GuideSearch({
  value,
  onChange,
  results,
  loading,
  onSelect,
  placeholder = "Cerca argomento (es. ferie, chiudere mese, Ctrl+F2)",
}) {
  const showDropdown = value.trim().length > 0;

  return (
    <div className="relative w-full max-w-2xl">
      <div className="relative">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-suite-neutral-400"
        />
        <Input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 pl-9 pr-9 text-sm"
          data-testid="guide-search-input"
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-suite-neutral-400 hover:bg-suite-neutral-100 hover:text-suite-neutral-700"
            aria-label="Pulisci ricerca"
            data-testid="guide-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-[420px] overflow-auto rounded-lg border border-suite-neutral-200 bg-white shadow-lg"
          data-testid="guide-search-results"
        >
          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-suite-neutral-500">
              <Loader2 size={14} className="animate-spin" />
              Ricerca in corso...
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-xs italic text-suite-neutral-500">
              Nessun risultato per <b>{value}</b>. Prova con un sinonimo o una
              parola piu' generica.
            </div>
          )}
          {!loading &&
            results.map((r) => (
              <button
                key={r.article_id}
                type="button"
                onClick={() => onSelect(r.chapter_id, r.article_id)}
                className="block w-full border-b border-suite-neutral-100 px-4 py-2.5 text-left last:border-0 hover:bg-suite-primary/5"
                data-testid={`guide-search-hit-${r.article_id}`}
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-suite-primary">
                  {r.chapter_title}
                </div>
                <div className="text-sm font-medium text-suite-neutral-900">
                  {r.article_title}
                </div>
                {r.highlights?.length > 0 && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-suite-neutral-500">
                    {r.highlights[0]}
                  </div>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
